import { BusinessDayCalculatorPL, CommunityTransactionForPurchaseQuery, CompanyParametersQuery, ContextKeys, CurrencyCollectionQuery, DocumentAdjustmentWrongInventoryEffectError, DocumentClientAdjustmentValidationBoPart, DocumentExchangeRateValidationBoPart, DocumentItemsNumberingBoPart, DocumentLinkKind, DocumentNumberBoPart, ExchangeRateMixin, NumberingQuery, PaymentMethodQuery, PurchaseKindQuery, ToPayMixin, VatRateForPurchaseQuery, collectionChangedJoinPointFilter, propertyChangedJoinPointFilter } from '@skylab-shared/business';
import { BO, BusinessObjectBase, CollectionChangedJoinPoint, PropertyChangedJoinPoint, QueryBus, Weaver } from '@skylab-shared/core';
import { InventoryEffect, ProductModel, PurchaseInvoiceAdjustmentDocumentModel, PurchaseInvoiceDocumentItemModel, PurchaseInvoiceDocumentModel } from '@skylab-shared/dataModel';
import { dateDiffInDays, isNumber, substractDays } from '@skylab-shared/utils';
import { ProductRepository } from '../product/product.repo';
import { PurchaseLinksMixin } from './mixins/purchase-links.mixin';
import { PurchaseDocumentCalculatorBoPart } from './parts/purchase-document-calculator.bo-part';
import { PurchaseDocumentJpkGroupsBOPart } from './parts/purchase-document-jpk-groups.bo-part';
import { PurchaseDocumentPaymentsBOPart } from './parts/purchase-document-payments.bo-part';
import { PurchaseDocumentValidationBOPart } from './parts/purchase-document-validation.bo-part';
import { PurchaseDocumentVatDeductionDateBOPart } from './parts/purchase-document-vat-deduction-date.bo-part';
import { PurchaseInvoiceIssuedToInventoryGoodsReceiptBoPart } from './parts/purchase-invoice-issued-to-inventory-goods-receipt.bo-part';
import { PurchaseDocumentItemsValidationsBoPart } from './parts/purchase-invoice-items-validations.bo-part';
import { PurchaseInvoiceVatTableValidationsBoPart } from './parts/purchase-invoice-vat-table-validations.bo-part';
import { refreshPurchaseInvoiceDocumentItemPriceAsync } from './utils/purchase-document-item-utils';
import { PurchaseInvoiceAdjustmentDeliveryDateEarlierThanAdjustedError } from './violations/purchase-invoice-adjustment-delivery-date-earlier-than-adjusted-error';
import { PurchaseInvoiceAdjustmentIssueDateEarlierThanAdjustedError } from './violations/purchase-invoice-adjustment-issue-date-earlier-than-adjusted-error';

export interface PurchaseInvoiceAdjustmentDocumentBO extends PurchaseLinksMixin<PurchaseInvoiceAdjustmentDocumentModel>, ToPayMixin<PurchaseInvoiceAdjustmentDocumentModel>, ExchangeRateMixin<PurchaseInvoiceAdjustmentDocumentModel> {};

@BO({
    modelType: PurchaseInvoiceAdjustmentDocumentModel,
    mixins: [PurchaseLinksMixin, ExchangeRateMixin, ToPayMixin]
})
export class PurchaseInvoiceAdjustmentDocumentBO extends BusinessObjectBase<PurchaseInvoiceAdjustmentDocumentModel> {

    public constructor(
        weaver: Weaver,
        protected readonly queryBus: QueryBus,
        protected readonly productRepository: ProductRepository
    ) {
        super(weaver);
        this.parts.push(new DocumentNumberBoPart(weaver, queryBus));
        this.parts.push(new DocumentExchangeRateValidationBoPart(weaver));
        this.parts.push(new PurchaseDocumentValidationBOPart(weaver, queryBus));
        this.parts.push(new PurchaseDocumentPaymentsBOPart(weaver));
        this.parts.push(new PurchaseDocumentJpkGroupsBOPart(weaver));
        this.parts.push(new PurchaseDocumentCalculatorBoPart(weaver));
        this.parts.push(new DocumentItemsNumberingBoPart(weaver));
        this.parts.push(new DocumentClientAdjustmentValidationBoPart(this.weaver, () => this.adjustedDocument));
        this.parts.push(new PurchaseDocumentItemsValidationsBoPart(weaver));
        this.parts.push(new PurchaseInvoiceIssuedToInventoryGoodsReceiptBoPart(weaver));
        this.parts.push(new PurchaseDocumentVatDeductionDateBOPart(weaver));
        this.parts.push(new PurchaseInvoiceVatTableValidationsBoPart(weaver, () => this.isDocumentAdjusted));
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        await Promise.all([
            this.localContext.preload(VatRateForPurchaseQuery),
            this.localContext.preload(CurrencyCollectionQuery),
            this.localContext.preload(CompanyParametersQuery),
            this.localContext.preload(PurchaseKindQuery),
            this.localContext.preload(CommunityTransactionForPurchaseQuery),
            this.localContext.preload(PaymentMethodQuery),
            this.localContext.preload(NumberingQuery),
            this.localContext.preload(ContextKeys.isInvoiceToInventoryGoodsReceipt(this.model.sun), () => Promise.resolve(this.linksInventory)),
        ]);

        if (this.model.isPersistent) {
            await this.loadLinksAsync();
            this.localContext.refresh(ContextKeys.isInvoiceToInventoryGoodsReceipt(this.model.sun));
            this.setInfoAboutAdjustedDocument();
        }

        this.registerDocumentReadonlyRules();
        this.registerDateValidationRules();
        this.registerItemRules();
        this.registerIssueGoodsReceiptAdjustmentStatusChange();
        this.registerWrongInventoryEffectOnDocumentValidationRule();
    }

    private registerWrongInventoryEffectOnDocumentValidationRule(): void {
        const inventoryEffectOrder = Object.values(InventoryEffect).filter(isNumber);
        const getInventoryEffectOrder = (inventoryEffect: InventoryEffect | null) => inventoryEffectOrder.findIndex(elem => elem === inventoryEffect);

        this.validation.addRule(
            [[PurchaseInvoiceAdjustmentDocumentModel, 'inventoryEffect']],
            ctx => {
                const previousDocumentInventoryEffectIndex = getInventoryEffectOrder(this.adjustedDocument?.inventoryEffect);
                if (previousDocumentInventoryEffectIndex < 0) return true;

                const currentInventoryEffectIndex = getInventoryEffectOrder(ctx.bo.model.inventoryEffect);
                return currentInventoryEffectIndex <= previousDocumentInventoryEffectIndex;
            },
            DocumentAdjustmentWrongInventoryEffectError
        );
    }

    private registerIssueGoodsReceiptAdjustmentStatusChange(): void {
        const handle = () => {
            this.model.issueGoodsReceiptAdjustment = this.model.items.reduce((prev, curr, currIdx) => {
                if (prev) return prev;

                const adjustedItem = this.adjustedDocument.items[currIdx];
                if (adjustedItem) return adjustedItem.includesInventoryControl && (adjustedItem.quantity !== curr.quantity);

                return curr.includesInventoryControl;
            }, false);
        };

        this.registerListening(this.weaver.register(CollectionChangedJoinPoint, collectionChangedJoinPointFilter(this)(this.model.items)(
            _ => handle()

        )));

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(PurchaseInvoiceDocumentItemModel, ['quantity', 'includesInventoryControl'])(
            _ => handle()
        )));
    }

    private registerItemRules(): void {
        this.readonly.setReadonlyOnSubtree(PurchaseInvoiceDocumentItemModel, () => this.isDocumentAdjusted, {
            exceptions: [
                [PurchaseInvoiceDocumentItemModel, <keyof PurchaseInvoiceDocumentItemModel>'note']
            ]
        });
    }

    private registerDateValidationRules(): void {
        this.validation.addRule(
            [[PurchaseInvoiceAdjustmentDocumentModel, 'issueDate']],
            ctx => this.adjustedDocument?.issueDate && ctx.source.issueDate ? dateDiffInDays(ctx.source.issueDate, this.adjustedDocument.issueDate) >= 0 : true,
            PurchaseInvoiceAdjustmentIssueDateEarlierThanAdjustedError
        );

        this.validation.addRule(
            [[PurchaseInvoiceAdjustmentDocumentModel, 'deliveryDate']],
            ctx => this.adjustedDocument ? dateDiffInDays(ctx.source.deliveryDate, this.adjustedDocument.deliveryDate) >= 0 : true,
            PurchaseInvoiceAdjustmentDeliveryDateEarlierThanAdjustedError
        );
        }

    private registerDocumentReadonlyRules(): void {
        const modelType = PurchaseInvoiceAdjustmentDocumentModel;

        this.readonly.setAsReadonly([modelType, 'calculationMethod']);
        this.readonly.setAsReadonly([modelType, 'costCalculationMethod']);
        this.readonly.setAsReadonly([modelType, 'currencyId']);

        this.readonly.addRule([
            [modelType, 'documentNumber'],
            [modelType, 'issueDate'],
            [modelType, 'issuer'],
            [modelType, 'numberingId'],
            [modelType, 'receiptDate'],
            [modelType, 'automaticVatCalculation'],
            [modelType, 'recipient'],
            [modelType, 'communityTransactionId'],
            [modelType, 'jpkVatGroups'],
            [modelType, 'bankAccount'],
            [modelType, 'transactionLocation'],
            [modelType, 'adjustmentReason'],
            [modelType, 'seller'],
            [modelType, 'externalDocumentNumber'],
            [modelType, 'paymentMethodId'],
            [modelType, 'dueDate'],
            [modelType, 'dueDateDays'],
            [modelType, 'cashBasis'],
            [modelType, 'splitPayment'],
            [modelType, 'fixedAsset'],
            [modelType, 'deliveryDate'],
        ], () => this.isDocumentAdjusted);
    }

    protected calculateDateForCurrencyExchangeRate(): Date {
        const date = substractDays(this.model.vatDeductionDate || this.model.receiptDate, 1);
        return BusinessDayCalculatorPL.getLastBusinessDay(date);
    }

    private _adjustedDocument: PurchaseInvoiceDocumentModel | PurchaseInvoiceAdjustmentDocumentModel;
    public set adjustedDocument(document: PurchaseInvoiceDocumentModel | PurchaseInvoiceAdjustmentDocumentModel) {
        this._adjustedDocument = document;
    }

    public get adjustedDocument() {
        return this._adjustedDocument;
    }

    private setInfoAboutAdjustedDocument(): void {
        if (this.links.adjustments) {
            const originalDocument = this.links.adjustments.filter(a => a.role === DocumentLinkKind.Original)[0];
            this.model.adjustedDocumentId = originalDocument.documentId;
            this.model.adjustedDocumentVersion = originalDocument.version;
        }
    }

    public async refreshDocumentItemsPricesAsync(): Promise<void> {
        await Promise.all(this.model.items.map(async item => {
            const product = item.productId ? await this.localContext.getAsync(ContextKeys.product(item.productId), () => this.productRepository.findAsync(item.productId!)) : new ProductModel();
            await refreshPurchaseInvoiceDocumentItemPriceAsync(item, this.localContext, this.model, product);
        }));
    }
}
